import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';

import { IProcessOrder } from '../../interfaces/IProcessOrder';
import { TSettings } from '../../services/appconfig/settings.model';
import { OrderProcessingService } from '../../services/order-processing/order-processing.service';
import { SettingsService } from '../../services/settings/settings.service';
import {
  FRONT_PATHS,
  ORDER_ID_TYPE,
  REAR_PATHS,
  RETAIL_PATHS,
  VIEW_TYPES,
} from '../../utils/constants';
import { Utils } from '../../utils/utils';

@Component({
  selector: 'frodo-delivery-provider-dialog',
  templateUrl: './delivery-provider-dialog.component.html',
  styleUrls: ['./delivery-provider-dialog.component.scss'],
})
export class DeliveryProviderDialogComponent implements OnInit, OnDestroy {
  timer = 0;
  initialTimer = 0;
  formattedTime: string;
  timerId: ReturnType<typeof setInterval> | null = null;
  destroy$ = new Subject<void>();
  guidedLoadFailForward: boolean;
  processOrder: IProcessOrder;
  timeoutConfigError = 'common.deliveryProviderDialog.timeoutConfigError';

  constructor(
    private dialogRef: MatDialogRef<DeliveryProviderDialogComponent>,
    private settingsService: SettingsService,
    private orderProcessingService: OrderProcessingService,
    private utils: Utils
  ) {
    this.processOrder = this.orderProcessingService.orderToProcess;
  }

  ngOnInit(): void {
    this.loadSettings();
  }

  loadSettings(): void {
    this.settingsService.getSettings.pipe(takeUntil(this.destroy$)).subscribe({
      next: (settings) => this.handleSettings(settings),
    });
  }

  handleSettings(settings: Partial<TSettings>): void {
    const timeout = settings.apps.frodo.timeouts.deliveryProvider;
    if (timeout == null) {
      console.error(this.timeoutConfigError);
      return;
    }
    this.guidedLoadFailForward = settings.device.guidedLoadFailForward;
    this.initialTimer = timeout / 1000;
    this.timer = this.initialTimer;
    this.formattedTime = this.formatTime(this.timer);
    this.startCountdown();
  }

  startCountdown() {
    this.timerId = setInterval(() => {
      if (this.timer > 0) {
        this.timer--;
        this.formattedTime = this.formatTime(this.timer);
      } else {
        this.stopCountdown();
      }
    }, 1000);
  }

  stopCountdown(): void {
    this.clearTimer();
    const action = this.guidedLoadFailForward ? this.skip : this.cancel;
    action.call(this);
  }

  clearTimer(): void {
    clearInterval(this.timerId);
    this.timerId = null;
  }

  resetCountdown() {
    this.clearCountdown();
    this.timer = this.initialTimer;
    this.startCountdown();
  }

  formatTime(seconds: number): string {
    return `0:${seconds.toString().padStart(2, '0')}`;
  }

  cancel(): void {
    const { type: cardType, frodoContext, flow } = this.processOrder;
    this.dialogRef.close(true);
    const path = this.getRedirectionPath(cardType, frodoContext);
    const url = `${frodoContext}${path}`;
    const params = { type: cardType, flow };
    this.utils.redirect(url, params);
  }

  getRedirectionPath(cardType: string, frodoContext: string): string {
    const paths = {
      [VIEW_TYPES.REAR_LOADING]:
        cardType === ORDER_ID_TYPE
          ? `/${REAR_PATHS.CREATE_ORDER}`
          : `/${REAR_PATHS.CREATE_ORDER_BY_PHONE}`,
      [VIEW_TYPES.FRONT_LOADING]: `/${FRONT_PATHS.CREATE_ORDER}`,
      [VIEW_TYPES.RETAIL]: `/${RETAIL_PATHS.CREATE_ORDER}`,
    };
    return paths[frodoContext] || '';
  }

  selectProvider(): void {
    this.dialogRef.close(false);
  }

  skip(): void {
    this.dialogRef.close(true);
    this.orderProcessingService.processOrder(this.processOrder);
  }

  clearCountdown(): void {
    if (this.timerId) {
      this.clearTimer();
    }
  }

  ngOnDestroy(): void {
    this.clearCountdown();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
